import type { Volume } from '~/shared/types/api';

import { ACTIVE_STATUSES, attachClassification } from './classification';
import type { ExplorationVolume } from '../types/exploration';

type VolumeWithAccount = Volume & { accountId?: string; account?: string };

function resolveVolumeAccountId(v: VolumeWithAccount): string {
  if (v.accountId) return v.accountId;
  if (typeof v.account === 'string' && v.account) return v.account;
  return '';
}

export function transformApiVolume(v: Volume): ExplorationVolume {
  const tags: Record<string, string> = {};
  if (Array.isArray(v.tags)) {
    v.tags.forEach((t) => {
      if (t.key) tags[t.key] = t.value || '';
    });
  }

  const sizeGiB = v.diskSize ? v.diskSize / (1024 * 1024 * 1024) : 0;
  const usedGiB = v.diskUsed ? v.diskUsed / (1024 * 1024 * 1024) : null;
  const datafyDiskSizeGiB = v.datafyDiskSize ? v.datafyDiskSize / (1024 * 1024 * 1024) : null;

  const dfData = v.isDatafiableData || {};
  const isPotentiallyDatafiable = dfData.isPotentiallyDatafiable === true;
  const isDatafiable = dfData.isDatafiable === true || v.isDatafiable === true;
  // Backend now names and ranks the reason itself — prefer the singular field
  // over the legacy plural list whenever it's present.
  const datafiableReasons = v.notDatafiableReason
    ? [v.notDatafiableReason]
    : dfData.notDatafiableReasons || [];

  let status = (v.monitoring || '').toLowerCase();
  if (!status) status = v.awsState === 'available' ? 'unattached' : 'unmonitored';
  if (ACTIVE_STATUSES.has(status)) status = 'managed';

  const cluster = v.clusterName && v.clusterName !== 'N/A' ? v.clusterName : null;
  const nodeGroup = v.nodeGroupName && v.nodeGroupName !== 'N/A' ? v.nodeGroupName : null;

  const base = {
    ebsId: v.volumeId || '',
    name: v.volumeName || '',
    size: Math.round(sizeGiB * 100) / 100,
    diskType: v.diskType || '',
    iops: v.iops ?? null,
    throughput: v.throughput ?? null,
    boot: Boolean(v.isBootDisk),
    status,
    cluster,
    nodeGroup,
    instanceId: v.instanceId || null,
    region: v.region || '',
    tags,
    datafiable: isPotentiallyDatafiable || isDatafiable,
    isPotentiallyDatafiable,
    isDatafiable,
    datafiableReasons,
    usedGiB,
    datafyDiskSize: datafyDiskSizeGiB,
    accountId: resolveVolumeAccountId(v),
    accountName: '',
    awsState: v.awsState || '',
    awsStatus: v.awsStatus || '',
    availabilityZone: v.availabilityZone || '',
  };

  return attachClassification(base) as ExplorationVolume;
}

const EXPLORATION_STATUS_TO_MONITORING: Record<string, Volume['monitoring']> = {
  managed: 'Managed',
  datafied: 'Managed',
  migrating: 'Migrating',
  unmigrating: 'Unmigrating',
  shrinking: 'Shrinking',
  growing: 'Growing',
  monitored: 'Monitored',
  unmonitored: 'Unmonitored',
  unattached: 'Unattached',
};

/** Minimal API volume for fleet AutoScale UI when only exploration cache fields exist. */
export function explorationVolumeToFleetVolume(dv: ExplorationVolume): Volume {
  const gib = 1024 * 1024 * 1024;
  const reason = dv.datafiableReasons[0];
  return {
    id: dv.ebsId,
    volumeId: dv.ebsId,
    volumeName: dv.name || dv.ebsId,
    instanceId: dv.instanceId ?? '',
    monitoring: EXPLORATION_STATUS_TO_MONITORING[dv.status] ?? 'Monitored',
    diskSize: Math.round(dv.size * gib),
    diskUsed: dv.usedGiB != null ? Math.round(dv.usedGiB * gib) : 0,
    diskType: dv.diskType as Volume['diskType'],
    datafyDiskSize: dv.datafyDiskSize != null ? Math.round(dv.datafyDiskSize * gib) : 0,
    datafyDiskUsed: 0,
    iops: dv.iops ?? 0,
    throughput: dv.throughput ?? 0,
    awsState: dv.awsState ?? '',
    awsStatus: dv.awsStatus ?? '',
    availabilityZone: dv.availabilityZone ?? '',
    region: dv.region,
    clusterName: dv.cluster ?? '',
    nodeGroupName: dv.nodeGroup ?? '',
    osImage: '',
    isBootDisk: false,
    yearlyEstimatedSavings: '0',
    yearlyRealizedSavings: '0',
    tags: Object.entries(dv.tags).map(([key, value]) => ({ key, value })),
    attachedToInstances: {},
    accountId: dv.accountId,
    isDatafiable: dv.isDatafiable,
    isUnDatafiable: false,
    notDatafiableReason: reason,
    isDatafiableData: {
      isDatafiable: dv.isDatafiable,
      isPotentiallyDatafiable: dv.isPotentiallyDatafiable,
      notDatafiableReasons: dv.datafiableReasons,
    },
  };
}
